import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Autocomplete,
    Card,
    Grid,
    CircularProgress,
    Divider
} from "@mui/material";

import { db } from "../../firebase";

import {
    collection,
    getDocs,
    query,
    where,
    Timestamp,
    orderBy,
    onSnapshot
} from "firebase/firestore";

function Paymodewisereport() {
    const today = new Date().toISOString().split("T")[0];

    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);

    const [payModes, setPayModes] = useState([]);
    const [selectedPayMode, setSelectedPayMode] = useState(null);

    const [customers, setCustomers] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    // ================= PAYMENT MODES & CUSTOMERS =================
    useEffect(() => {
        const unsubPayModes = onSnapshot(
            collection(db, "payment_modes"),
            (snap) => {
                const data = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setPayModes(data);
            }
        );

        const unsubCustomers = onSnapshot(
            collection(db, "customers"),
            (snap) => {
                const data = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setCustomers(data);
            }
        );

        return () => {
            unsubPayModes();
            unsubCustomers();
        };
    }, []);

    // ================= FETCH REPORT FROM customer_history =================
    const fetchReport = async () => {
        if (!fromDate || !toDate) {
            return alert("Please select date range");
        }

        setLoading(true);

        try {
            const start = Timestamp.fromDate(new Date(`${fromDate}T00:00:00`));
            const end = Timestamp.fromDate(new Date(`${toDate}T23:59:59`));

            const q = query(
                collection(db, "customer_history"),
                where("date", ">=", start),
                where("date", "<=", end),
                orderBy("date", "asc")
            );

            const snap = await getDocs(q);

            const historyDocs = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // Create a quick lookup map for customer names
            const customerMap = {};
            customers.forEach(c => {
                customerMap[c.id] = c.customerName;
            });

           let rows = [];

            historyDocs.forEach(item => {
                const resolvedCustomerName = customerMap[item.customerId] || item.customerName;
                if (!resolvedCustomerName) {
                    return;
                }
                const itemPayMode = item.paymentMode || item.mode || "";
                if (
                    selectedPayMode &&
                    itemPayMode !== selectedPayMode.payMode
                ) {
                    return;
                }

                rows.push({
                    id: item.id,
                    customerName: resolvedCustomerName,
                    payMode: itemPayMode || "-",
                    amount: Number(item.amount) || 0,
                    note: item.note || "",
                    paymentDate: item.date || null
                });
            });

            setReportData(rows);
        } catch (error) {
            console.error(error);
            alert("Error loading report");
        } finally {
            setLoading(false);
        }
    };

    // ================= TOTAL =================
    const totalAmount = reportData.reduce(
        (sum, item) => sum + Number(item.amount || 0),
        0
    );

    return (
        <Box p={2}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
                Pay Mode Wise Report (Customer History)
            </Typography>

            <Grid container className="no-print row" alignItems="center">
                <Grid className="col-sm-3 mb-2" item xs={12} sm={3}>
                    <Autocomplete
                        options={payModes}
                        value={selectedPayMode}
                        onChange={(event, newValue) => setSelectedPayMode(newValue)}
                        getOptionLabel={(option) => option?.payMode || ""}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Filter By Pay Mode"
                                size="small"
                                fullWidth
                            />
                        )}
                    />
                </Grid>

                <Grid className="col-sm-3 mb-2" item xs={12} sm={2}>
                    <TextField
                        fullWidth
                        label="From"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                </Grid>

                <Grid className="col-sm-3 mb-2" item xs={12} sm={2}>
                    <TextField
                        fullWidth
                        label="To"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                </Grid>

                <Grid className="col-sm-3 mb-2" item xs={12} sm={3}>
                    <Button
                        variant="contained"
                        onClick={fetchReport}
                        disabled={loading}
                        sx={{ mr: 1 }}
                    >
                        {loading ? <CircularProgress size={22} /> : "Generate"}
                    </Button>

                    <Button variant="outlined" onClick={() => window.print()}>
                        Print
                    </Button>
                </Grid>
            </Grid>

            {/* ================= REPORT PRINT AREA ================= */}
            <Card sx={{ p: 2, mt: 2 }}>
                <Box className="printable-area" p={2}>
                    <Box sx={{ border: "2px solid #000", p: 2 }}>
                        {/* HEADER */}
                        <Box textAlign="center" mb={2}>
                            <Typography
                                variant="subtitle2"
                                sx={{ mt: 1, fontWeight: "bold", letterSpacing: 1 }}
                            >
                                PAY MODE WISE REPORT
                            </Typography>
                            <Typography variant="body2">
                                {fromDate} TO {toDate}
                            </Typography>

                            {selectedPayMode && (
                                <Typography variant="body1" fontWeight="bold">
                                    Payment Mode : {selectedPayMode.payMode}
                                </Typography>
                            )}

                            <Typography variant="body1" sx={{ mt: 1, fontWeight: "bold" }}>
                                Total Entries : {reportData.length}
                            </Typography>

                            <Divider sx={{ my: 2, bgcolor: "#000", height: "2px" }} />
                        </Box>

                        {/* TABLE */}
                        <Paper sx={{ width: "100%", overflow: "hidden" }}>
                            <Table
                                size="small"
                                sx={{
                                    border: "2px solid #000",
                                    "& th, & td": {
                                        border: "1px solid #000",
                                        padding: "6px"
                                    }
                                }}
                            >
                                <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                                    <TableRow>
                                        <TableCell><strong>Sr</strong></TableCell>
                                        <TableCell><strong>Payment Date</strong></TableCell>
                                        <TableCell><strong>Customer Name</strong></TableCell>
                                        <TableCell><strong>Pay Mode</strong></TableCell>
                                        <TableCell align="right"><strong>Amount</strong></TableCell>
                                        <TableCell><strong>Note</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>
                                                {row.paymentDate
                                                    ? row.paymentDate.toDate().toLocaleDateString("en-GB")
                                                    : "-"}
                                            </TableCell>
                                            <TableCell>{row.customerName}</TableCell>
                                            <TableCell>{row.payMode}</TableCell>
                                            <TableCell align="right">
                                                ₹{row.amount.toLocaleString()}
                                            </TableCell>
                                            <TableCell>{row.note}</TableCell>
                                        </TableRow>
                                    ))}

                                    {/* GRAND TOTAL */}
                                    {reportData.length > 0 && (
                                        <TableRow sx={{ bgcolor: "#eee" }}>
                                            <TableCell colSpan={4} align="right">
                                                <strong>GRAND TOTAL</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>₹{totalAmount.toLocaleString()}</strong>
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    )}

                                    {reportData.length === 0 && !loading && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                No data found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>
                </Box>
            </Card>

            {/* ================= PRINT STYLE CSS ================= */}
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    .printable-area, .printable-area * {
                        visibility: visible;
                    }
                    .printable-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    .no-print {
                        display: none !important;
                    }
                    @page {
                        size: A4 landscape;
                        margin: 10mm;
                    }
                }
            `}</style>
        </Box>
    );
}

export default Paymodewisereport;